/**
 * The URL is the state.
 *
 * Every filter control on an experiment writes a search param rather than
 * local state. That is what makes "Share view" a copy of `window.location.href`
 * and what makes a screenshot reproducible from its URL alone.
 *
 * This module is deliberately router-agnostic — `@charphq/ui` has no
 * `@tanstack/react-router` dependency. Each route passes
 * `parseExperimentSearch` straight to `validateSearch`.
 */

import { SAFE_ENVELOPES } from "./envelopes";
import {
  CLIMATE_YEARS,
  isClimateYearId,
  type ClimateYear,
  type GroupBy,
  type Metric,
  type Source,
} from "./types";

export interface ExperimentSearch {
  metric: Metric;
  source: Source;
  groupBy: GroupBy;
  /** Envelope id, e.g. "bizot". */
  envelope: string;
  year: ClimateYear;
}

export const DEFAULT_EXPERIMENT_SEARCH: ExperimentSearch = {
  metric: "temperature",
  source: "historical",
  groupBy: "region",
  envelope: "bizot",
  // The most recent year, which is what every screen opens on.
  year: CLIMATE_YEARS[CLIMATE_YEARS.length - 1],
};

const METRICS: readonly Metric[] = ["temperature", "humidity"];
const SOURCES: readonly Source[] = ["historical", "live"];
const GROUP_BYS: readonly GroupBy[] = ["region", "country", "buildingType", "site"];

function oneOf<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return typeof value === "string" && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : fallback;
}

/**
 * Validate a year by **shape**, not by membership in a fixed list.
 *
 * Which years exist depends on what the backend actually holds, and this function runs in the
 * router's `validateSearch` — before any data has loaded. So it admits any well-formed id and
 * leaves "is there data for it?" to the screen, which clamps to `dataset.climate.years`. Checking
 * membership here would reject a valid link the moment retention shifted.
 */
function climateYear(value: unknown, fallback: ClimateYear): ClimateYear {
  return isClimateYearId(value) ? value : fallback;
}

/**
 * Coerce arbitrary URL search params into a complete, valid `ExperimentSearch`.
 *
 * Never throws and never returns a partial object: an unknown or malformed
 * value falls back to its default, so a hand-edited URL degrades to a sensible
 * view instead of a crashed route.
 */
export function parseExperimentSearch(search: Record<string, unknown>): ExperimentSearch {
  return {
    metric: oneOf(search.metric, METRICS, DEFAULT_EXPERIMENT_SEARCH.metric),
    source: oneOf(search.source, SOURCES, DEFAULT_EXPERIMENT_SEARCH.source),
    groupBy: oneOf(search.groupBy, GROUP_BYS, DEFAULT_EXPERIMENT_SEARCH.groupBy),
    envelope: oneOf(
      search.envelope,
      SAFE_ENVELOPES.map((envelope) => envelope.id),
      DEFAULT_EXPERIMENT_SEARCH.envelope,
    ),
    year: climateYear(search.year, DEFAULT_EXPERIMENT_SEARCH.year),
  };
}

/* --------------------------------------------------------- live network -- */

export const LIVE_NETWORK_VIEWS = ["globe", "map", "flow"] as const;
export type LiveNetworkView = (typeof LIVE_NETWORK_VIEWS)[number];

export const STATUS_FILTERS = ["all", "critical", "warning", "healthy"] as const;
export type StatusFilter = (typeof STATUS_FILTERS)[number];

/**
 * Live Network's own search params — and *only* its own.
 *
 * Deliberately not extending `ExperimentSearch`. This screen has no filter bar:
 * no metric, no group-by, no envelope, no year, and its source is always live.
 * Since the parsed object is what the router writes back to the URL, inheriting
 * those five fields meant every Live Network navigation stamped
 * `metric=temperature&source=historical&groupBy=region&envelope=bizot&year=…`
 * into the address — five defaults for controls the screen does not have, and
 * any stale climate state preserved along with them.
 *
 * `view` is always present, because it is the single source of truth for which
 * visualisation is mounted and a missing param would make "which view am I on"
 * a question with two answers.
 *
 * `tour` and `demo` are the literal `1` or absent, rather than booleans. A
 * boolean would produce `?tour=true` where the ticket (and every shared link in
 * the design) says `?tour=1`, and `?tour=false` where a clean URL is wanted.
 */
export interface LiveNetworkSearch {
  view: LiveNetworkView;
  status: StatusFilter;
  tour?: 1;
  demo?: 1;
}

export const DEFAULT_LIVE_NETWORK_SEARCH: LiveNetworkSearch = {
  view: "globe",
  status: "all",
};

/** Accept `1`, `"1"`, `true` and `"true"`; anything else means off. */
function flag(value: unknown): 1 | undefined {
  return value === 1 || value === "1" || value === true || value === "true" ? 1 : undefined;
}

export function parseLiveNetworkSearch(search: Record<string, unknown>): LiveNetworkSearch {
  const parsed: LiveNetworkSearch = {
    view: oneOf(search.view, LIVE_NETWORK_VIEWS, DEFAULT_LIVE_NETWORK_SEARCH.view),
    status: oneOf(search.status, STATUS_FILTERS, DEFAULT_LIVE_NETWORK_SEARCH.status),
  };
  // Assigned conditionally so an "off" flag leaves no key behind, which is what
  // keeps `/experiments/live-network?view=globe` free of `&tour=&demo=`.
  const tour = flag(search.tour);
  if (tour) parsed.tour = tour;
  const demo = flag(search.demo);
  if (demo) parsed.demo = demo;
  return parsed;
}

/* ----------------------------------------------------------- climate loop -- */

/**
 * The group-by slices the Climate Loop offers.
 *
 * "Anonymous site" is deliberately absent. On a screen whose unit is a closed
 * loop, grouping by site means 165 overlapping paths — not a chart, and not what
 * the ticket's acceptance criteria ask for (they name these three). The need it
 * would have served is met better by `isolate`, which promotes one site to the
 * focus tier *without* throwing away the peers it should be read against.
 */
export const LOOP_GROUP_BYS: readonly GroupBy[] = ["region", "country", "buildingType"];

/**
 * Climate Loop's own search params.
 *
 * Same reasoning as `LiveNetworkSearch`: the parsed object is what the router
 * writes back to the URL, so extending `ExperimentSearch` would stamp
 * `metric=temperature` into every navigation — a control this screen does not
 * have, because both axes are already in use.
 *
 * `highlight` and `isolate` are separate axes, not one control with two names.
 * `highlight` names a *group* (the ranking panel's rows); `isolate` names a
 * single climate site, which layers on top of whatever grouping is active.
 */
export interface ClimateLoopSearch {
  source: Source;
  groupBy: GroupBy;
  /** Envelope id, e.g. "bizot". */
  envelope: string;
  year: ClimateYear;
  /** Group key of the highlighted loop, e.g. "Flanders". Absent means the first row. */
  highlight?: string;
  /** Climate site id, e.g. "cs-014". Absent means no site is isolated. */
  isolate?: string;
  /** Replace site names with `#NNN` aliases. */
  anonymise?: 1;
}

export const DEFAULT_CLIMATE_LOOP_SEARCH: ClimateLoopSearch = {
  source: "historical",
  groupBy: "region",
  envelope: "bizot",
  year: CLIMATE_YEARS[CLIMATE_YEARS.length - 1],
};

/**
 * Shape of a climate site id.
 *
 * Two forms, because there are two datasets: `cs-014` from the generator, and a UUID from the real
 * backend, where a site is a deployment. Validated rather than passed through, so
 * `?isolate=<script>` never reaches a lookup — and so a stale id degrades to "no isolation" rather
 * than an empty focus tier.
 */
const SITE_ID = /^(cs-\d{3}|[0-9a-fA-F-]{36})$/;

function trimmed(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const text = value.trim();
  return text.length > 0 ? text : undefined;
}

/**
 * Coerce arbitrary URL search params into a complete, valid `ClimateLoopSearch`.
 *
 * Never throws. A hand-edited `groupBy=site` falls back to `region` rather than
 * rendering 165 loops, and an unparseable `isolate` is dropped entirely.
 */
export function parseClimateLoopSearch(search: Record<string, unknown>): ClimateLoopSearch {
  const parsed: ClimateLoopSearch = {
    source: oneOf(search.source, SOURCES, DEFAULT_CLIMATE_LOOP_SEARCH.source),
    groupBy: oneOf(search.groupBy, LOOP_GROUP_BYS, DEFAULT_CLIMATE_LOOP_SEARCH.groupBy),
    envelope: oneOf(
      search.envelope,
      SAFE_ENVELOPES.map((envelope) => envelope.id),
      DEFAULT_CLIMATE_LOOP_SEARCH.envelope,
    ),
    year: climateYear(search.year, DEFAULT_CLIMATE_LOOP_SEARCH.year),
  };

  // Assigned conditionally, so an absent control leaves no key behind and a
  // pristine view keeps a clean address — the same rule Live Network follows.
  const highlight = trimmed(search.highlight);
  if (highlight) parsed.highlight = highlight;

  const isolate = trimmed(search.isolate);
  if (isolate && SITE_ID.test(isolate)) parsed.isolate = isolate;

  const anonymise = flag(search.anonymise);
  if (anonymise) parsed.anonymise = anonymise;

  return parsed;
}

/* ------------------------------------------------------- seasonal climate -- */

/**
 * The group-by slices the Seasonal Climate wheel offers.
 *
 * All four the ticket names, "Anonymous site" included. Unlike the Climate Loop — where grouping
 * by site means 165 overlapping paths and no chart — rings stack, so a site-level wheel stays
 * readable; the model caps how many it draws and says how many it left out.
 */
export const SEASONAL_GROUP_BYS: readonly GroupBy[] = ["region", "country", "buildingType", "site"];

/**
 * Seasonal Climate's own search params.
 *
 * Same reasoning as `LiveNetworkSearch` and `ClimateLoopSearch`: the parsed object is what the
 * router writes back, so extending `ExperimentSearch` would stamp defaults for controls this
 * screen does not have.
 *
 * `highlight` pins a ring; **hover is not here**. Hover fires on every mousemove, and writing a
 * search param per frame would bury the previous page under a thousand history entries and fight
 * the state-stability rules in ADR 0011. Pinning is a click, and a click is worth an entry.
 */
export interface SeasonalClimateSearch {
  metric: Metric;
  source: Source;
  groupBy: GroupBy;
  /** Envelope id, e.g. "bizot". */
  envelope: string;
  year: ClimateYear;
  /** Group key of the pinned ring, e.g. "Flanders". Absent means none is pinned. */
  highlight?: string;
  /** Replace site names with `#NNN` aliases. */
  anonymise?: 1;
  /** `synthetic` opts out of real data — for demos, screenshots and reproducible tests. */
  data?: "synthetic";
}

export const DEFAULT_SEASONAL_CLIMATE_SEARCH: SeasonalClimateSearch = {
  metric: "temperature",
  source: "historical",
  groupBy: "region",
  envelope: "bizot",
  year: CLIMATE_YEARS[CLIMATE_YEARS.length - 1],
};

/**
 * Coerce arbitrary URL search params into a complete, valid `SeasonalClimateSearch`.
 *
 * Never throws. An unknown group-by falls back to `region`, and a year is checked for shape only —
 * the screen clamps it to what the dataset actually holds.
 */
export function parseSeasonalClimateSearch(search: Record<string, unknown>): SeasonalClimateSearch {
  const parsed: SeasonalClimateSearch = {
    metric: oneOf(search.metric, METRICS, DEFAULT_SEASONAL_CLIMATE_SEARCH.metric),
    source: oneOf(search.source, SOURCES, DEFAULT_SEASONAL_CLIMATE_SEARCH.source),
    groupBy: oneOf(search.groupBy, SEASONAL_GROUP_BYS, DEFAULT_SEASONAL_CLIMATE_SEARCH.groupBy),
    envelope: oneOf(
      search.envelope,
      SAFE_ENVELOPES.map((envelope) => envelope.id),
      DEFAULT_SEASONAL_CLIMATE_SEARCH.envelope,
    ),
    year: climateYear(search.year, DEFAULT_SEASONAL_CLIMATE_SEARCH.year),
  };

  // Assigned conditionally, so an untouched control leaves no key behind.
  const highlight = trimmed(search.highlight);
  if (highlight) parsed.highlight = highlight;

  const anonymise = flag(search.anonymise);
  if (anonymise) parsed.anonymise = anonymise;

  if (search.data === "synthetic") parsed.data = "synthetic";

  return parsed;
}

/**
 * Drop params that match the default, so a pristine view has a clean URL and a
 * shared link only carries what the sharer actually changed.
 */
export function stripDefaultSearch(search: ExperimentSearch): Partial<ExperimentSearch> {
  const out: Partial<ExperimentSearch> = {};
  for (const key of Object.keys(DEFAULT_EXPERIMENT_SEARCH) as (keyof ExperimentSearch)[]) {
    if (search[key] !== DEFAULT_EXPERIMENT_SEARCH[key]) {
      out[key] = search[key] as never;
    }
  }
  return out;
}
